import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Filter, Download, Plus, Hand, Bookmark, BookmarkPlus, X, MoveRight, UserCog, Check, Trash2, ChevronLeft, ChevronRight, Mail, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { StageBadge, TagBadge } from "../components/StageBadge";
import CreateLeadDialog from "../components/CreateLeadDialog";
import BulkSendEmailDialog from "../components/BulkSendEmailDialog";
import OwnerMultiSelect from "../components/OwnerMultiSelect";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { api, cachedGet, formatApiError } from "../lib/api";
import { TAGS } from "../lib/stages";

const SELLER_TAG_LABELS = { 1: "Brand", 2: "Magic", 3: "Drop-Shipper", 4: "Trader", 5: "Aggregator" };
import { useStages } from "../lib/useStages";
import { useAuth, normalizeRole } from "../lib/auth";

export default function Workbench() {
  const { user } = useAuth();
  const { stages: STAGES, taggingRequired: STAGES_REQUIRING_TAG } = useStages();
  const isManager = user && ["super_admin", "admin", "sales_manager"].includes(normalizeRole(user.role));
  const isSuperAdmin = user && normalizeRole(user.role) === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(searchParams.get("create") === "1");
  const [transcriptLead, setTranscriptLead] = useState(null);
  const [transcriptCalls, setTranscriptCalls] = useState([]);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [remarksCache, setRemarksCache] = useState({});
  const [hoveredRemarks, setHoveredRemarks] = useState({ id: null, text: null, x: 0, y: 0 });
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveBox, setShowSaveBox] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const stage = searchParams.get("lead_stage") || "";
  const stageValues = useMemo(() => stage ? stage.split(",").filter(Boolean) : [], [stage]);
  const pickable = searchParams.get("pickable") === "true";
  const mine = searchParams.get("mine") === "true";
  const aiVerified = searchParams.get("ai_verified") === "true";
  const tag = searchParams.get("stage_tagging") || "";
  const tagValues = useMemo(() => tag ? tag.split(",").filter(Boolean) : [], [tag]);
  const ownerId = searchParams.get("lead_owner_id") || "";
  const ownerIds = useMemo(() => ownerId ? ownerId.split(",").filter(Boolean) : [], [ownerId]);
  const utmSource = searchParams.get("utm_source") || "";
  const utmSourceValues = useMemo(() => utmSource ? utmSource.split(",").filter(Boolean) : [], [utmSource]);
  const leadSource = searchParams.get("lead_source") || "";
  const leadSourceValues = useMemo(() => leadSource ? leadSource.split(",").filter(Boolean) : [], [leadSource]);
  const sellerTag = searchParams.get("seller_tag") || "";
  const sellerTagValues = useMemo(() => sellerTag ? sellerTag.split(",").filter(Boolean) : [], [sellerTag]);
  const createdFrom = searchParams.get("created_from") || "";
  const createdTo = searchParams.get("created_to") || "";
  const actFrom = searchParams.get("last_activity_from") || "";
  const actTo = searchParams.get("last_activity_to") || "";
  const qualifiedMonth = searchParams.get("qualified_month") || "";
  const qualifiedMode = searchParams.get("qualified_mode") || "";
  const liveMonth = searchParams.get("live_month") || "";
  const reachedGroup = searchParams.get("reached_group") || "";

  // Pagination
  const pageSize = Number(searchParams.get("page_size") || 100);
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const skip = (page - 1) * pageSize;
  const sort = searchParams.get("sort") || "latest";

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [utmSources, setUtmSources] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [bulkStageOpen, setBulkStageOpen] = useState(false);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkStage, setBulkStage] = useState("Junk");
  const [bulkTag, setBulkTag] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkOwner, setBulkOwner] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkStaging, setBulkStaging] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const selectAllAcrossPages = async () => {
    if (selectingAll || total <= items.length) return;
    setSelectingAll(true);
    try {
      const params = new URLSearchParams();
      Object.entries(currentFilters).forEach(([k, v]) => params.set(k, v));
      const { data } = await api.get(`/leads/ids?${params.toString()}`);
      const ids = data.ids || [];
      setSelected(new Set(ids));
      toast.success(`${ids.length} lead(s) selected across all pages`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't select all");
    } finally {
      setSelectingAll(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const currentFilters = useMemo(() => {
    const o = {};
    if (q) o.q = q;
    if (stage) o.lead_stage = stage;
    if (tag) o.stage_tagging = tag;
    if (ownerId) o.lead_owner_id = ownerId;
    if (utmSource) o.utm_source = utmSource;
    if (leadSource) o.lead_source = leadSource;
    if (sellerTag) o.seller_tag = sellerTag;
    if (pickable) o.pickable = "true";
    if (mine) o.mine = "true";
    if (createdFrom) o.created_from = createdFrom;
    if (createdTo) o.created_to = createdTo;
    if (actFrom) o.last_activity_from = actFrom;
    if (actTo) o.last_activity_to = actTo;
    if (aiVerified) o.ai_verified = "true";
    if (qualifiedMonth) o.qualified_month = qualifiedMonth;
    if (qualifiedMode) o.qualified_mode = qualifiedMode;
    if (liveMonth) o.live_month = liveMonth;
    if (reachedGroup) o.reached_group = reachedGroup;
    return o;
  }, [q, stage, tag, ownerId, pickable, mine, createdFrom, createdTo, actFrom, actTo, aiVerified, qualifiedMonth, qualifiedMode, liveMonth, reachedGroup, utmSource, leadSource, sellerTag]);

  const fetchList = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(currentFilters).forEach(([k, v]) => params.set(k, v));
    params.set("limit", String(pageSize));
    params.set("skip", String(skip));
    params.set("view", "summary");
    params.set("sort", sort);
    try {
      const { data } = await api.get(`/leads?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleNameHover = async (lead) => {
    if (remarksCache[lead.id] !== undefined) {
      setHoveredRemarks(prev => ({ ...prev, id: lead.id, text: remarksCache[lead.id] }));
      return;
    }
    try {
      const { data } = await api.get(`/leads/${lead.id}`);
      const text = data.remarks || "";
      setRemarksCache((prev) => ({ ...prev, [lead.id]: text }));
      setHoveredRemarks(prev => ({ ...prev, id: lead.id, text }));
    } catch {
      setRemarksCache((prev) => ({ ...prev, [lead.id]: "" }));
    }
  };

  const openTranscript = async (lead) => {
    setTranscriptLead(lead);
    setTranscriptCalls([]);
    setTranscriptLoading(true);
    try {
      const { data } = await api.get(`/ai-calls?lead_id=${lead.id}`);
      setTranscriptCalls(Array.isArray(data) ? data : []);
    } catch {
      setTranscriptCalls([]);
    } finally {
      setTranscriptLoading(false);
    }
  };

  const fetchSavedViews = async () => {
    try {
      const { data } = await api.get("/saved-views");
      setSavedViews(data);
    } catch (err) {
      console.warn("Failed to load saved views:", err);
    }
  };

  useEffect(() => {
    fetchList();
     
  }, [stage, tag, ownerId, utmSource, leadSource, sellerTag, pickable, mine, aiVerified, createdFrom, createdTo, actFrom, actTo, qualifiedMonth, qualifiedMode, liveMonth, reachedGroup, page, pageSize, sort]);

  useEffect(() => {
    fetchSavedViews();
    (async () => {
      try {
        const [u, c] = await Promise.all([
          cachedGet("/users"),
          cachedGet("/masters?type=category&active_only=true"),
        ]);
        setUsers((u.data || []).filter((x) => x.active !== false));
        setCategories(c.data || []);
      } catch (err) {
        console.warn("Failed to load users/categories:", err);
      }
    })();
    (async () => {
      try {
        const { data } = await cachedGet("/leads/_meta/utm-sources");
        setUtmSources(data || []);
      } catch (err) {
        console.warn("Failed to load UTM sources:", err);
      }
    })();
    (async () => {
      try {
        const { data } = await cachedGet("/leads/_meta/lead-sources");
        setLeadSources(data || []);
      } catch (err) {
        console.warn("Failed to load lead sources:", err);
      }
    })();
  }, []);

  // Clear selection when filters change
  useEffect(() => {
    setSelected(new Set());
  }, [stage, tag, ownerId, utmSource, leadSource, sellerTag, pickable, mine, aiVerified, createdFrom, createdTo, actFrom, actTo, qualifiedMonth, qualifiedMode, liveMonth, reachedGroup, page, pageSize, sort]);

  const updateFilter = (key, value) => {
    const sp = new URLSearchParams(searchParams);
    if (value) sp.set(key, value);
    else sp.delete(key);
    // Any filter change resets pagination back to page 1
    sp.delete("page");
    setSearchParams(sp);
  };

  const goToPage = (p) => {
    const sp = new URLSearchParams(searchParams);
    if (p <= 1) sp.delete("page"); else sp.set("page", String(p));
    setSearchParams(sp);
  };

  const setPageSizeNew = (sz) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("page_size", String(sz));
    sp.delete("page");
    setSearchParams(sp);
  };

  const clearAllFilters = () => {
    const sp = new URLSearchParams();
    setQ("");
    setSearchParams(sp);
  };

  const activeFilterCount = Object.keys(currentFilters).length;

  const doBulkStage = async () => {
    if (bulkStaging) return;
    setBulkStaging(true);
    try {
      const body = {
        lead_ids: Array.from(selected),
        to_stage: bulkStage,
      };
      if (STAGES_REQUIRING_TAG.has(bulkStage)) body.tag = bulkTag || null;
      if (bulkStage === "Not-Interested") body.lost_reason = bulkReason || null;
      const { data } = await api.post("/leads/bulk-stage", body);
      setBulkResult(data);
      if (data.updated > 0) {
        const emailNote = data.emails_queued ? ` · ${data.emails_queued} email(s) queued` : "";
        toast.success(`Moved ${data.updated} lead(s)${emailNote}`);
      }
      if (data.failed?.length > 0) toast.error(`${data.failed.length} lead(s) couldn't be moved — see details`);
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk move failed");
    } finally {
      setBulkStaging(false);
    }
  };

  const doBulkReassign = async () => {
    if (!bulkOwner) return toast.error("Pick a new owner");
    try {
      const { data } = await api.post("/leads/bulk-reassign", {
        lead_ids: Array.from(selected),
        new_owner_id: bulkOwner,
      });
      toast.success(`Reassigned ${data.updated} lead(s)`);
      setBulkReassignOpen(false);
      clearSelection();
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reassign failed");
    }
  };

  const doBulkCategory = async () => {
    if (!bulkCategory) return toast.error("Pick a category");
    try {
      const { data } = await api.post("/leads/bulk-category", {
        lead_ids: Array.from(selected),
        category: bulkCategory,
      });
      toast.success(`Updated category on ${data.updated} lead(s)`);
      setBulkCategoryOpen(false);
      clearSelection();
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Category update failed");
    }
  };

  const doBulkDelete = async () => {
    if (bulkDeleteConfirm !== "DELETE") {
      return toast.error('Type DELETE (in caps) to confirm');
    }
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/leads/bulk-delete", {
        lead_ids: Array.from(selected),
      });
      toast.success(`Deleted ${data.deleted} lead(s)`);
      setBulkDeleteOpen(false);
      setBulkDeleteConfirm("");
      clearSelection();
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchList();
  };

  const togglePickable = () => {
    const sp = new URLSearchParams(searchParams);
    if (pickable) sp.delete("pickable");
    else sp.set("pickable", "true");
    setSearchParams(sp);
  };

  const toggleMine = () => {
    const sp = new URLSearchParams(searchParams);
    if (mine) sp.delete("mine");
    else sp.set("mine", "true");
    setSearchParams(sp);
  };

  const toggleAiVerified = () => {
    const sp = new URLSearchParams(searchParams);
    if (aiVerified) sp.delete("ai_verified");
    else sp.set("ai_verified", "true");
    setSearchParams(sp);
  };

  const applyView = (v) => {
    const sp = new URLSearchParams();
    Object.entries(v.filters || {}).forEach(([k, val]) => {
      if (val !== undefined && val !== null && val !== "" && val !== false) sp.set(k, String(val));
    });
    setQ(v.filters?.q || "");
    setSearchParams(sp);
  };

  const saveView = async () => {
    const name = newViewName.trim();
    if (!name) return toast.error("Name required");
    try {
      await api.post("/saved-views", { name, filters: currentFilters });
      toast.success(`Saved view “${name}”`);
      setNewViewName("");
      setShowSaveBox(false);
      fetchSavedViews();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const deleteView = async (v, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/saved-views/${v.id}`);
      toast.success("View deleted");
      fetchSavedViews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const exportCsv = async () => {
    // Delegates to backend /leads/export.csv which returns comprehensive CSV
    // with ALL detail-window fields + per-stage entry timestamps in one shot.
    // Server does the stage_history join (single Mongo query for all leads).
    try {
      const params = new URLSearchParams();
      Object.entries(currentFilters).forEach(([k, v]) => params.set(k, v));
      params.set("limit", "50000");
      const { data } = await api.get(`/leads/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      if (!data || data.size === 0) return toast.message("No leads match the current filters");
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export complete");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    }
  };

  const pickLead = async (id, e) => {
    e.stopPropagation();
    try {
      await api.post(`/leads/${id}/pick`);
      toast.success("Lead picked");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Pick failed");
    }
  };

  return (
    <Layout title="Lead Workbench" breadcrumb={["Leads", "Workbench"]}>
      {/* Saved Views bar */}
      {(savedViews.length > 0 || showSaveBox) && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3 shadow-sm" data-testid="saved-views-bar">
          <div className="flex flex-wrap items-center gap-2">
            <Bookmark size={14} className="text-orange-500 ml-1" />
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mr-1">Saved views</span>
            {savedViews.map((v) => (
              <button
                key={v.id}
                data-testid={`saved-view-${v.id}`}
                onClick={() => applyView(v)}
                className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 hover:bg-orange-100 border border-orange-200 text-xs font-medium text-orange-700"
              >
                {v.name}
                <span
                  onClick={(e) => deleteView(v, e)}
                  className="opacity-0 group-hover:opacity-100 ml-1 text-orange-500 hover:text-red-600"
                  title="Delete view"
                >
                  <X size={12} />
                </span>
              </button>
            ))}
            {savedViews.length === 0 && (
              <span className="text-xs text-slate-400">No saved views yet — save your first filter combo.</span>
            )}
            {showSaveBox && (
              <div className="flex items-center gap-1 ml-auto">
                <input
                  data-testid="saved-view-name-input"
                  autoFocus
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveView()}
                  placeholder="View name…"
                  className="text-xs border border-slate-200 rounded-md px-2 py-1 w-40 outline-none focus:border-orange-300"
                />
                <button data-testid="saved-view-save-btn" onClick={saveView} className="text-xs font-medium px-2.5 py-1 rounded-md bg-orange-500 text-white">Save</button>
                <button onClick={() => setShowSaveBox(false)} className="p-1 text-slate-400 hover:text-slate-700"><X size={12} /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={onSearch} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 flex-1 min-w-[220px]">
            <Search size={14} className="text-slate-400" />
            <input
              data-testid="workbench-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by company, POC, email, phone…"
              className="bg-transparent text-sm outline-none flex-1"
            />
          </form>

          <MultiSelectFilter
            testIdPrefix="filter-stage"
            items={STAGES.map((s) => ({ value: s, label: s }))}
            selectedValues={stageValues}
            onChange={(vals) => updateFilter("lead_stage", vals.join(","))}
            allLabel="All stages"
            singularLabel="stages"
          />

          <MultiSelectFilter
            testIdPrefix="filter-tag"
            items={TAGS.map((t) => ({ value: t, label: t }))}
            selectedValues={tagValues}
            onChange={(vals) => updateFilter("stage_tagging", vals.join(","))}
            allLabel="All tags"
            singularLabel="tags"
          />

          <OwnerMultiSelect
            testIdPrefix="filter-owner"
            users={users}
            selectedIds={ownerIds}
            onChange={(ids) => updateFilter("lead_owner_id", ids.join(","))}
          />

          <MultiSelectFilter
            testIdPrefix="filter-utm-source"
            items={utmSources.map((s) => ({ value: s, label: s }))}
            selectedValues={utmSourceValues}
            onChange={(vals) => updateFilter("utm_source", vals.join(","))}
            allLabel="All UTM sources"
            singularLabel="UTM sources"
          />

          <MultiSelectFilter
            testIdPrefix="filter-lead-source"
            items={leadSources.map((s) => ({ value: s, label: s }))}
            selectedValues={leadSourceValues}
            onChange={(vals) => updateFilter("lead_source", vals.join(","))}
            allLabel="All lead sources"
            singularLabel="lead sources"
          />

          <MultiSelectFilter
            testIdPrefix="filter-seller-tag"
            items={[
              ...Object.entries(SELLER_TAG_LABELS).map(([value, label]) => ({ value, label })),
              { value: "none", label: "No seller tag" },
            ]}
            selectedValues={sellerTagValues}
            onChange={(vals) => updateFilter("seller_tag", vals.join(","))}
            allLabel="All seller tags"
            singularLabel="seller tags"
          />

          <button
            data-testid="filter-pickable-toggle"
            onClick={togglePickable}
            className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
              pickable ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"
            }`}
          >
            Pickable
          </button>
          <button
            data-testid="filter-mine-toggle"
            onClick={toggleMine}
            className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
              mine ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"
            }`}
          >
            My leads
          </button>
          <button
            data-testid="filter-ai-verified-toggle"
            onClick={toggleAiVerified}
            className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
              aiVerified ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"
            }`}
          >
            AI Verified
          </button>

          <div className="flex-1" />

          <button
            data-testid="workbench-save-view-btn"
            onClick={() => setShowSaveBox(true)}
            disabled={activeFilterCount === 0}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={activeFilterCount === 0 ? "Apply a filter or search to save a view" : "Save current filters as a view"}
          >
            <BookmarkPlus size={14} /> Save view
          </button>
          {(user?.role === "admin" || user?.role === "super_admin") && (
            <button
              data-testid="workbench-export-btn"
              onClick={exportCsv}
              className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 flex items-center gap-1.5 hover:bg-slate-50"
            >
              <Download size={14} /> Export
            </button>
          )}
          <button
            data-testid="workbench-create-btn"
            onClick={() => setCreateOpen(true)}
            className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1.5"
          >
            <Plus size={14} /> New lead
          </button>
        </div>

        {/* Second row: date ranges + clear */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mr-1">Created</span>
          <input
            data-testid="filter-created-from"
            type="date"
            value={createdFrom}
            onChange={(e) => updateFilter("created_from", e.target.value)}
            className="border border-slate-200 rounded-md text-sm px-2 py-1.5 bg-white"
            title="Created from"
          />
          <span className="text-xs text-slate-400">→</span>
          <input
            data-testid="filter-created-to"
            type="date"
            value={createdTo}
            onChange={(e) => updateFilter("created_to", e.target.value)}
            className="border border-slate-200 rounded-md text-sm px-2 py-1.5 bg-white"
            title="Created to"
          />

          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 ml-4 mr-1">Last activity</span>
          <input
            data-testid="filter-activity-from"
            type="date"
            value={actFrom}
            onChange={(e) => updateFilter("last_activity_from", e.target.value)}
            className="border border-slate-200 rounded-md text-sm px-2 py-1.5 bg-white"
            title="Last activity from"
          />
          <span className="text-xs text-slate-400">→</span>
          <input
            data-testid="filter-activity-to"
            type="date"
            value={actTo}
            onChange={(e) => updateFilter("last_activity_to", e.target.value)}
            className="border border-slate-200 rounded-md text-sm px-2 py-1.5 bg-white"
            title="Last activity to"
          />

          <div className="flex-1" />

          {activeFilterCount > 0 && (
            <button
              data-testid="filter-clear-all"
              onClick={clearAllFilters}
              className="text-xs font-medium px-3 py-1.5 rounded-md text-orange-600 hover:bg-orange-50 inline-flex items-center gap-1"
            >
              <X size={12} /> Clear filters ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* Drilldown context banner — surfaces when the user came from Report 2 */}
      {qualifiedMonth && (
        <div
          data-testid="qualified-scope-banner"
          className="mb-3 px-4 py-2.5 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-between"
        >
          <div className="text-sm text-slate-700">
            Showing leads first-qualified in <b>{qualifiedMonth}</b>
            {qualifiedMode === "same_month" && <> (same-month qualifications only — creation month also = {qualifiedMonth})</>}
            {qualifiedMode !== "same_month" && <> (any creation month)</>}
            . <span className="text-slate-500">Filters like Stage / Owner still apply on top.</span>
          </div>
          <button
            onClick={() => {
              const sp = new URLSearchParams(searchParams);
              sp.delete("qualified_month");
              sp.delete("qualified_mode");
              setSearchParams(sp);
            }}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          >
            <X size={12} /> Remove
          </button>
        </div>
      )}

      {/* Drilldown context banner — surfaces when the user came from Report 1 */}
      {(liveMonth || reachedGroup) && (
        <div
          data-testid="report1-scope-banner"
          className="mb-3 px-4 py-2.5 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-between"
        >
          <div className="text-sm text-slate-700">
            {liveMonth && <>Showing leads that <b>entered the Live stage in {liveMonth}</b>.</>}
            {reachedGroup && <>Showing leads that <b>ever reached {(
              { fu_plus: "Follow-up or later", interested_plus: "Interested or later", oib_plus: "Agreement / Onboarding / Live", live: "Live" }[reachedGroup] || reachedGroup
            )}</b>.</>}
            {" "}<span className="text-slate-500">Filters like Stage / Owner still apply on top.</span>
          </div>
          <button
            onClick={() => {
              const sp = new URLSearchParams(searchParams);
              sp.delete("live_month");
              sp.delete("reached_group");
              setSearchParams(sp);
            }}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          >
            <X size={12} /> Remove
          </button>
        </div>
      )}
      {/* "Select all across pages" banner — only when every row on the current page
          is selected AND there are more rows on other pages matching the filters. */}
      {selected.size > 0 && selected.size >= items.length && items.length > 0 && total > items.length && (
        <div className="mb-3 px-4 py-2.5 rounded-md bg-orange-50 border border-orange-200 flex items-center justify-between" data-testid="select-all-banner">
          <div className="text-sm text-slate-700">
            {selected.size >= total ? (
              <>All <b>{total}</b> matching leads are selected across all pages.</>
            ) : (
              <>All <b>{items.length}</b> leads on this page are selected. <span className="text-slate-500">{total - items.length} more match these filters.</span></>
            )}
          </div>
          {selected.size < total ? (
            <button
              data-testid="select-all-across"
              onClick={selectAllAcrossPages}
              disabled={selectingAll}
              className="text-sm font-medium text-orange-700 hover:text-orange-800 hover:underline disabled:opacity-50"
            >
              {selectingAll ? "Selecting…" : `Select all ${total} across all pages`}
            </button>
          ) : (
            <button
              data-testid="clear-all-across"
              onClick={clearSelection}
              className="text-sm font-medium text-slate-600 hover:text-slate-800 hover:underline"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-medium text-slate-700">
            {loading ? "Loading…" : (
              total > 0 ? (
                <>Showing <b>{skip + 1}–{Math.min(skip + items.length, total)}</b> of <b>{total}</b> leads</>
              ) : "0 leads"
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Sort by</label>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => {
                  const sp = new URLSearchParams(searchParams);
                  if (e.target.value === "latest") sp.delete("sort"); else sp.set("sort", e.target.value);
                  sp.delete("page");
                  setSearchParams(sp);
                }}
                className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
              >
                <option value="latest">Latest first (default)</option>
                <option value="oldest">Oldest first</option>
                <option value="company_asc">Company A → Z</option>
                <option value="company_desc">Company Z → A</option>
                <option value="owner_asc">Owner A → Z</option>
                <option value="stage">Stage</option>
                <option value="last_activity">Last activity (recent first)</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Per page</label>
              <select
                data-testid="page-size-select"
                value={pageSize}
                onChange={(e) => setPageSizeNew(Number(e.target.value))}
                className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
              >
                {[50, 100, 200, 500].map((sz) => <option key={sz} value={sz}>{sz}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="workbench-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    data-testid="select-all-checkbox"
                    type="checkbox"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleSelectAll}
                    className="accent-orange-500 cursor-pointer"
                  />
                </th>
                {["Company", "Stage", "Tag", "Owner", "Service Interest", "Monthly shipping vol.", "POC", "Category", "Seller Tag", "Lead date", "Last activity", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  className={`border-t border-slate-100 transition-colors ${
                    selected.has(l.id) ? "bg-orange-50/50" : "hover:bg-orange-50/30"
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      data-testid={`select-row-${l.id}`}
                      type="checkbox"
                      checked={selected.has(l.id)}
                      onChange={() => toggleSelect(l.id)}
                      className="accent-orange-500 cursor-pointer"
                    />
                  </td>
                  <td
                    className="px-4 py-3 font-medium text-slate-900 cursor-pointer relative"
                    onClick={() => navigate(`/leads/${l.id}`)}
                    onMouseEnter={(e) => { handleNameHover(l); setHoveredRemarks(prev => ({ ...prev, x: e.clientX, y: e.clientY })); }}
                    onMouseMove={(e) => setHoveredRemarks(prev => prev.id === l.id ? { ...prev, x: e.clientX, y: e.clientY } : prev)}
                    onMouseLeave={() => setHoveredRemarks({ id: null, text: null, x: 0, y: 0 })}
                  >
                    {l.ai_verified && (
                      <span
                        className="inline-block mb-1 px-2 py-0.5 rounded-b text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); openTranscript(l); }}
                      >
                        AI Verified
                      </span>
                    )}
                    <div>{l.company_name}</div>
                  </td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}><StageBadge stage={l.lead_stage} /></td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}><TagBadge tag={l.stage_tagging} /></td>
                  <td className="px-4 py-3 text-slate-700 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>{l.lead_owner_name || "—"}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)} data-testid={`service-interest-${l.id}`}>
                    {l.ai_service_interest ? (
                      <div className="flex flex-wrap gap-1">
                        {l.ai_service_interest.split(",").map((svc) => {
                          const s = svc.trim();
                          if (!s) return null;
                          return (
                            <span
                              key={s}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                            >
                              {s}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-right tabular-nums cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>
                    {l.monthly_shipping_volume != null ? Number(l.monthly_shipping_volume).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-700 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>
                    <div className="text-slate-900">{l.client_poc_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{l.client_poc_email || l.client_poc_number || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>{l.category || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)} data-testid={`seller-tag-cell-${l.id}`}>{SELLER_TAG_LABELS[l.seller_tag] || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>
                    {l.lead_date ? new Date(l.lead_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>
                    {l.last_activity_at ? new Date(l.last_activity_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        title="View AI call transcript"
                        onClick={(e) => { e.stopPropagation(); openTranscript(l); }}
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-orange-500"
                      >
                        <PhoneCall size={14} />
                      </button>
                      {l.is_pickable && (
                        <button
                          data-testid={`pick-btn-${l.id}`}
                          onClick={(e) => pickLead(l.id, e)}
                          className="text-xs font-medium px-2.5 py-1 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-700 inline-flex items-center gap-1"
                        >
                          <Hand size={12} /> Pick
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-sm text-slate-500">
                    No leads match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination footer */}
        {total > pageSize && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40" data-testid="pagination-footer">
            <div className="text-xs text-slate-500">
              Page <b className="text-slate-800">{page}</b> of <b className="text-slate-800">{Math.max(1, Math.ceil(total / pageSize))}</b>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="pagination-first"
                disabled={page === 1 || loading}
                onClick={() => goToPage(1)}
                className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="First page"
              >
                «
              </button>
              <button
                data-testid="pagination-prev"
                disabled={page === 1 || loading}
                onClick={() => goToPage(page - 1)}
                className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <ChevronLeft size={12} /> Prev
              </button>
              <button
                data-testid="pagination-next"
                disabled={page >= Math.ceil(total / pageSize) || loading}
                onClick={() => goToPage(page + 1)}
                className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                Next <ChevronRight size={12} />
              </button>
              <button
                data-testid="pagination-last"
                disabled={page >= Math.ceil(total / pageSize) || loading}
                onClick={() => goToPage(Math.ceil(total / pageSize))}
                className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Last page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateLeadDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          const sp = new URLSearchParams(searchParams);
          sp.delete("create");
          setSearchParams(sp);
        }}
        onCreated={() => {
          setCreateOpen(false);
          fetchList();
        }}
      />

      {/* Floating bulk action toolbar */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-action-toolbar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl px-4 py-2 flex items-center gap-2"
        >
          <span data-testid="bulk-selected-count" className="text-sm font-medium">
            {selected.size} selected
          </span>
          <span className="text-slate-500">•</span>
          <button
            data-testid="bulk-stage-btn"
            onClick={() => { setBulkResult(null); setBulkStageOpen(true); }}
            className="text-sm font-medium px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 inline-flex items-center gap-1.5"
          >
            <MoveRight size={14} /> Move stage
          </button>
          <button
            data-testid="bulk-category-btn"
            onClick={() => setBulkCategoryOpen(true)}
            className="text-sm font-medium px-3 py-1.5 rounded-full bg-white text-slate-900 inline-flex items-center gap-1.5"
          >
            <Filter size={14} /> Change category
          </button>
          {isManager && (
            <button
              data-testid="bulk-email-btn"
              onClick={() => setBulkEmailOpen(true)}
              className="text-sm font-medium px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center gap-1.5"
            >
              <Mail size={14} /> Send email
            </button>
          )}
          {isManager && (
            <button
              data-testid="bulk-reassign-btn"
              onClick={() => setBulkReassignOpen(true)}
              className="text-sm font-medium px-3 py-1.5 rounded-full bg-white text-slate-900 inline-flex items-center gap-1.5"
            >
              <UserCog size={14} /> Reassign
            </button>
          )}
          {isSuperAdmin && (
            <button
              data-testid="bulk-delete-btn"
              onClick={() => { setBulkDeleteConfirm(""); setBulkDeleteOpen(true); }}
              className="text-sm font-medium px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button
            data-testid="bulk-clear-btn"
            onClick={clearSelection}
            className="text-sm text-slate-400 hover:text-white p-1.5"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Bulk stage modal */}
      {bulkStageOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-stage-dialog" onClick={() => setBulkStageOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Move {selected.size} lead(s) to a new stage</h3>
              <button onClick={() => setBulkStageOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-3">
              <select data-testid="bulk-stage-select" value={bulkStage} onChange={(e) => setBulkStage(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {STAGES_REQUIRING_TAG.has(bulkStage) && (
                <div className="flex gap-2">
                  {TAGS.map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`bulk-tag-${t.toLowerCase()}`}
                      onClick={() => setBulkTag(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${bulkTag === t ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
              {bulkStage === "Not-Interested" && (
                <input
                  data-testid="bulk-lost-reason"
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="Lost reason (e.g. Pricing Issue)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md"
                />
              )}
              {bulkResult && (
                <div className="mt-3 text-sm">
                  {bulkResult.updated > 0 && (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md p-2 mb-2">
                      ✓ Moved {bulkResult.updated} lead(s).
                    </div>
                  )}
                  {bulkResult.failed?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-2 text-red-700">
                      <div className="font-semibold mb-1">{bulkResult.failed.length} lead(s) couldn&apos;t move:</div>
                      <ul className="text-xs max-h-32 overflow-y-auto space-y-1">
                        {bulkResult.failed.map((f) => (
                          <li key={f.lead_id}><b>{f.company_name}</b>: {f.missing_fields?.length ? `missing ${f.missing_fields.join(", ")}` : f.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
              <button onClick={() => setBulkStageOpen(false)} disabled={bulkStaging} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white disabled:opacity-50">Close</button>
              <button
                data-testid="bulk-stage-submit"
                onClick={doBulkStage}
                disabled={bulkStaging}
                className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium inline-flex items-center gap-1.5"
              >
                {bulkStaging ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                      <path d="M12 2 a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Moving {selected.size} lead(s)…
                  </>
                ) : (
                  <>Move stage</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk reassign modal */}
      {bulkReassignOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-reassign-dialog" onClick={() => setBulkReassignOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Reassign {selected.size} lead(s)</h3>
              <button onClick={() => setBulkReassignOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-6">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">New owner</label>
              <select data-testid="bulk-reassign-owner" value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md">
                <option value="">Choose owner…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
              <button onClick={() => setBulkReassignOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
              <button data-testid="bulk-reassign-submit" onClick={doBulkReassign} className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium">Reassign</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk category modal */}
      {bulkCategoryOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-category-dialog" onClick={() => setBulkCategoryOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Change category for {selected.size} lead(s)</h3>
              <button onClick={() => setBulkCategoryOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-6">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Category</label>
              <select
                data-testid="bulk-category-select"
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md"
              >
                <option value="">Choose category…</option>
                {categories.map((c) => <option key={c.id} value={c.value}>{c.value}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-2">Each change writes to the audit log on the affected lead.</p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
              <button onClick={() => setBulkCategoryOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
              <button data-testid="bulk-category-submit" onClick={doBulkCategory} className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium">Apply category</button>
            </div>
          </div>
        </div>
      )}
      <BulkSendEmailDialog
        open={bulkEmailOpen}
        leadIds={Array.from(selected)}
        sampleLead={items.find((l) => selected.has(l.id))}
        onClose={() => setBulkEmailOpen(false)}
        onSent={() => { setBulkEmailOpen(false); clearSelection(); }}
      />

      {/* Bulk delete confirmation (super_admin only) */}
      {bulkDeleteOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-delete-dialog" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-red-700 inline-flex items-center gap-2">
                <Trash2 size={16} /> Permanently delete {selected.size} lead(s)?
              </h3>
              <button onClick={() => !bulkDeleting && setBulkDeleteOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                This action is <b>irreversible</b>. The selected leads along with all their activities, scheduled tasks and audit logs will be hard-deleted from the database.
              </div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Type <span className="text-red-600 font-mono">DELETE</span> to confirm
              </label>
              <input
                data-testid="bulk-delete-confirm-input"
                autoFocus
                value={bulkDeleteConfirm}
                onChange={(e) => setBulkDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md font-mono"
              />
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
              <button
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="bulk-delete-submit"
                onClick={doBulkDelete}
                disabled={bulkDeleting || bulkDeleteConfirm !== "DELETE"}
                className="px-4 py-2 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <Trash2 size={14} /> {bulkDeleting ? "Deleting…" : `Delete ${selected.size} lead(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Remarks tooltip — fixed so it's never clipped by table overflow */}
      {hoveredRemarks.id && hoveredRemarks.text && (
        <div
          className="fixed z-[9999] w-60 bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl leading-relaxed pointer-events-none"
          style={{ left: hoveredRemarks.x + 12, top: hoveredRemarks.y - 8 }}
        >
          {hoveredRemarks.text}
        </div>
      )}

      {/* AI Call Transcript Drawer */}
      {transcriptLead && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-900/30" onClick={() => setTranscriptLead(null)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-md z-50 bg-white shadow-2xl flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{transcriptLead.company_name}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                  <PhoneCall size={11} /> AI Call Transcript
                </div>
              </div>
              <button onClick={() => setTranscriptLead(null)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {transcriptLoading ? (
                <div className="flex items-center justify-center h-32 text-sm text-slate-400">Loading…</div>
              ) : transcriptCalls.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-sm text-slate-400 gap-2">
                  <PhoneCall size={24} className="text-slate-300" />
                  No AI calls found for this lead.
                </div>
              ) : (
                transcriptCalls.map((call) => (
                  <div key={call.id} className="mb-6">
                    {/* Call meta */}
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                        call.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                      }`}>{call.status}</span>
                      {call.outcome && call.outcome !== "unknown" && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700">{call.outcome}</span>
                      )}
                      {call.service_interest && (
                        <span className="text-[11px] text-slate-500">{call.service_interest}</span>
                      )}
                      <span className="ml-auto text-[11px] text-slate-400">
                        {call.completed_at ? new Date(call.completed_at).toLocaleString() : new Date(call.created_at).toLocaleString()}
                      </span>
                    </div>

                    {/* Transcript bubbles */}
                    {call.transcript ? (
                      <div className="space-y-2">
                        {call.transcript.split("\n").filter(line => line.trim()).map((line, i) => {
                          const isAssistant = line.startsWith("assistant:");
                          const isUser = line.startsWith("user:");
                          if (!isAssistant && !isUser) return null;
                          const text = line.replace(/^(assistant|user):\s*\[?'?/, "").replace(/'?\]?\s*$/, "").trim();
                          if (!text) return null;
                          return (
                            <div key={i} className={`flex ${isAssistant ? "justify-start" : "justify-end"}`}>
                              <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-snug ${
                                isAssistant
                                  ? "bg-slate-100 text-slate-800 rounded-tl-sm"
                                  : "bg-orange-500 text-white rounded-tr-sm"
                              }`}>
                                <div className="text-[10px] font-semibold mb-0.5 opacity-60 uppercase">
                                  {isAssistant ? "AI" : call.customer_name || "Customer"}
                                </div>
                                {text}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400 italic">No transcript available.</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
